import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { TanStackTableRoot } from '@toggle/design-system';
import { generatePath } from '@toggle/helpers';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useThemeChildren } from '~/hooks/use-knowledge-graph/useKnowledgeGraph';
import { appPaths } from '~/routes/app-paths';
import { queryKeys } from '~/routes/query-params';

import { COUNT_COLUMN_ID, getColumns } from './getColumnUtils';
import { LoaderCell } from './loader-cell/LoaderCell';
import * as S from './ParentCategoryTable.styles';

export interface ParentCategoryTableProps {
  entityTag?: string;
  isExposureOnly: boolean;
  parentThemeId: string;
  childThemeId: string;
}

export const ParentCategoryTable = ({
  entityTag,
  isExposureOnly,
  parentThemeId,
  childThemeId,
}: ParentCategoryTableProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation(['widget']);
  const navigate = useNavigate();

  const {
    data = [],
    isFetching,
    error,
  } = useThemeChildren(parentThemeId, entityTag);

  const tableData = useMemo(
    () =>
      data.filter(item => (isExposureOnly ? item.is_company_exposed : true)),
    [data, isExposureOnly]
  );

  const columns = useMemo(
    () => getColumns({ t, total: tableData.length }),
    [tableData.length]
  );

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (error) {
    throw error;
  }

  return (
    <S.TableWrapper ref={containerRef}>
      <TanStackTableRoot data-testid="parent-category-table">
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                return (
                  <S.StyledTableHeader
                    key={header.id}
                    isHoverEnabled={false}
                    alignment={
                      header.column.id === COUNT_COLUMN_ID ? 'end' : 'start'
                    }
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </S.StyledTableHeader>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {isFetching ? (
            <LoaderCell
              columnLength={table.getHeaderGroups()[0].headers.length}
            />
          ) : (
            table.getRowModel().rows.map(row => (
              <S.StyledTableRow
                key={row.id}
                onClick={() => {
                  const requestPath = generatePath({
                    basePath: `${appPaths.themeOverview}/${row.original.theme.id}`,
                    params: {
                      [queryKeys.activeSubTab]: 'overview',
                    },
                  });

                  navigate(requestPath);
                }}
                $isActive={row.original.theme.id === childThemeId}
              >
                {row.getVisibleCells().map(cell => {
                  return (
                    <S.StyledTableCell
                      key={cell.id}
                      alignment={
                        cell.column.id === COUNT_COLUMN_ID ? 'right' : 'left'
                      }
                      isNumber={cell.column.id === COUNT_COLUMN_ID}
                      $isActive={row.original.theme.id === childThemeId}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </S.StyledTableCell>
                  );
                })}
              </S.StyledTableRow>
            ))
          )}
        </tbody>
      </TanStackTableRoot>
    </S.TableWrapper>
  );
};

ParentCategoryTable.Styled = S;
